import { FC } from "react";
import { GraphLink, GraphLinkLabel } from "../../models/GraphLink";
import {
  ReactFlow,
  Controls,
  Background,
  Position,
  MarkerType,
  Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useNavigate } from "react-router-dom";

interface IGraphViewProps {
  id: string;
  type: string;
  types: string[];
  positions?: ("left" | "right" | "up" | "down")[];
  graphData?: GraphLink[];
  labels?: GraphLinkLabel[];
}

const GraphView: FC<IGraphViewProps> = ({
  id,
  type,
  types,
  positions,
  graphData,
  labels,
}) => {
  const navigate = useNavigate();

  const getStyle = (label: any) => {
    let backgroundColor = "lightgray";
    const color = "black";
    switch (label?.type) {
      case "batch":
        backgroundColor = "#75cfff";
        break;
      case "invoice":
        backgroundColor = "#dedee3";
        break;
      case "inventory":
        backgroundColor = "#faff75";
        break;
      default:
        break;
    }
    return {
      backgroundColor,
      color,
    };
  };

  const getPositionDirection = (
    dir: string
  ): {
    source: Position;
    target: Position;
  } => {
    switch (dir) {
      case "left":
        return { source: Position.Left, target: Position.Right };
      case "right":
        return { source: Position.Right, target: Position.Left };
      case "up":
        return { source: Position.Top, target: Position.Bottom };
      case "down":
        return { source: Position.Bottom, target: Position.Top };
      default:
        return { source: Position.Right, target: Position.Left };
    }
  };

  const primaryAnchorMap = types.reduce(
    (acc, targetType, i) => {
      const direction = positions?.[i];
      if (!direction) return acc;
      const { source } = getPositionDirection(direction);
      acc[direction] = source;
      return acc;
    },
    {} as Record<string, Position>
  );

  const groupedLinks = types.reduce(
    (acc, targetType, i) => {
      const direction = positions?.[i] || "right";
      const relatedLinks = (graphData || []).filter(
        (link) =>
          (link.type[0] === type && link.type[1] === targetType) ||
          (link.type[1] === type && link.type[0] === targetType)
      );
      acc.push({ type: targetType, direction, links: relatedLinks });
      return acc;
    },
    [] as { type: string; direction: string; links: GraphLink[] }[]
  );

  const getGapForDirection = (dir: string) => {
    switch (dir) {
      case "up":
      case "down":
        return 60; // tighter for vertical stack
      case "left":
      case "right":
      default:
        return 100; // more spacing for side placement
    }
  };

  const linkedNodes: Node[] = groupedLinks.flatMap(
    ({ type: targetType, direction, links }) => {
      const { source, target } = getPositionDirection(direction);

      return links
        .map((link, i, arr) => {
          const isSource = link.type[0] === type;
          const otherId = isSource ? link.to : link.from;
          const labelMatch = labels?.find((l) => l.value === otherId);
          if (!labelMatch) return null;

          const gap = getGapForDirection(direction);
          const groupSize = arr.length;
          const offset = ((groupSize - 1) * gap) / 2;

          let x = 0,
            y = 0;

          switch (direction) {
            case "right":
              x = 250;
              y = i * gap - offset;
              break;
            case "left":
              x = -250;
              y = i * gap - offset;
              break;
            case "down":
              x = 0;
              y = 100 + i * gap;
              break;
            case "up":
              x = 0;
              y = -100 - i * gap;
              break;
            default:
              x = 250;
              y = i * gap - offset;
          }

          return {
            id: otherId,
            position: { x, y },
            data: { label: labelMatch.label },
            sourcePosition: isSource ? source : undefined,
            targetPosition: !isSource ? target : undefined,
            style: getStyle(labelMatch),
          } satisfies Node;
        })
        .filter(Boolean) as Node[];
    }
  );

  const baseLabel = labels?.find((l) => l.value === id);
  const baseStyle = {
    ...getStyle(baseLabel),
    fontWeight: 600,
    border: "3px solid #333",
  };

  // Create one directional main node per edge group
  const mainNodes = (positions || []).map((direction) => {
    const { source } = getPositionDirection(direction);
    const directionalId = `${id}-${direction}`; // <- make ID unique

    return {
      id: directionalId,
      position: { x: 0, y: 0 },
      data: { label: baseLabel?.label },
      sourcePosition: source,
      style: baseStyle,
    };
  });

  const nodes = [...mainNodes, ...linkedNodes];

  const edges = (graphData || []).map((link) => {
    const isSource = link.type[0] === type;

    const directionIndex = types.findIndex(
      (targetType) =>
        (link.type[0] === type && link.type[1] === targetType) ||
        (link.type[1] === type && link.type[0] === targetType)
    );

    const direction = positions?.[directionIndex] || "right";
    const directionalMainNodeId = `${id}-${direction}`;

    return {
      id: `${link.from}-${link.to}`,
      source: isSource ? directionalMainNodeId : link.to,
      target: isSource ? link.to : directionalMainNodeId,
      type: "smoothstep",
      animated: true,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: "#333",
      },
    };
  });

  return (
    <div
      style={{
        height: "500px",
        width: "100%",
        border: "1px solid #ccc",
        borderRadius: "4px",
        padding: "10px",
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        onNodeClick={(event, node) => {
          if (node.id.startsWith("batch-")) {
            navigate(`/batches/${node.id}`);
          }
          if (node.id.startsWith("inventory-")) {
            navigate(`/inventory/${node.id}`);
          }
        }}
      >
        <Background />
        <Controls />
      </ReactFlow>
    </div>
  );
};

export default GraphView;
